import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import CssBaseline from '@mui/material/CssBaseline';
import getTheme from '../theme/index';
import AOS from 'aos';
import {ConfirmProvider} from "material-ui-confirm";

const kThemeModeStorageKey = 'themeMode';
const kThemePreferenceLight = 'light';
const kThemePreferenceDark = 'dark';
const kThemePreferenceSystem = 'system';

const isValidThemePreference = (value) =>
    value === kThemePreferenceLight || value === kThemePreferenceDark || value === kThemePreferenceSystem;

const getSystemThemeMode = () => {
    if (typeof window === 'undefined' || !window.matchMedia) {
        return kThemePreferenceLight;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? kThemePreferenceDark
        : kThemePreferenceLight;
};

const ThemeModeContext = React.createContext({
    themeMode: kThemePreferenceLight,
    themePreference: kThemePreferenceSystem,
    setThemePreference: () => {},
    themeToggler: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

export const useDarkMode = () => {
    const [themePreference, setThemePreferenceState] = useState(kThemePreferenceSystem);
    const [systemThemeMode, setSystemThemeMode] = useState(kThemePreferenceLight);
    const [mountedComponent, setMountedComponent] = useState(false);

    const setThemePreference = useCallback((preference) => {
        const nextPreference = isValidThemePreference(preference) ? preference : kThemePreferenceSystem;
        try {
            window.localStorage.setItem(kThemeModeStorageKey, nextPreference);
        } catch {
            /* do nothing */
        }
        setThemePreferenceState(nextPreference);
    }, []);

    const themeMode = themePreference === kThemePreferenceSystem ? systemThemeMode : themePreference;

    const themeToggler = useCallback(() => {
        themeMode === kThemePreferenceLight
            ? setThemePreference(kThemePreferenceDark)
            : setThemePreference(kThemePreferenceLight);
    }, [themeMode, setThemePreference]);

    useEffect(() => {
        let mediaQueryList = null;

        const updateSystemThemeMode = () => {
            setSystemThemeMode(getSystemThemeMode());
        };

        try {
            const localThemePreference = window.localStorage.getItem(kThemeModeStorageKey);
            if (isValidThemePreference(localThemePreference)) {
                setThemePreferenceState(localThemePreference);
            } else {
                setThemePreferenceState(kThemePreferenceSystem);
            }
        } catch {
            setThemePreferenceState(kThemePreferenceSystem);
        }

        updateSystemThemeMode();
        if (window.matchMedia) {
            mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
            if (mediaQueryList.addEventListener) {
                mediaQueryList.addEventListener('change', updateSystemThemeMode);
            } else if (mediaQueryList.addListener) {
                mediaQueryList.addListener(updateSystemThemeMode);
            }
        }

        setMountedComponent(true);

        return () => {
            if (!mediaQueryList) {
                return;
            }
            if (mediaQueryList.removeEventListener) {
                mediaQueryList.removeEventListener('change', updateSystemThemeMode);
            } else if (mediaQueryList.removeListener) {
                mediaQueryList.removeListener(updateSystemThemeMode);
            }
        };
    }, []);

    return [themeMode, themeToggler, mountedComponent, themePreference, setThemePreference];
};

export default function Page({ children }) {
    React.useEffect(() => {
        // Remove the server-side injected CSS.
        const jssStyles = document.querySelector('#jss-server-side');
        if (jssStyles) {
            jssStyles.parentElement.removeChild(jssStyles);
        }

        AOS.init({
            once: true,
            delay: 0,
            duration: 800,
            offset: 0,
            easing: 'ease-in-out',
        });
    }, []);

    const [themeMode, themeToggler, mountedComponent, themePreference, setThemePreference] = useDarkMode();

    const themeModeContextValue = useMemo(() => ({
        themeMode,
        themePreference,
        setThemePreference,
        themeToggler,
    }), [themeMode, themePreference, setThemePreference, themeToggler]);

    useEffect(() => {
        AOS.refresh();
        document.documentElement.setAttribute('data-color-mode', themeMode);
        document.documentElement.setAttribute('data-theme-preference', themePreference);
    }, [mountedComponent, themeMode, themePreference]);

    return (
        <ConfirmProvider>
        <ThemeModeContext.Provider value={themeModeContextValue}>
            <ThemeProvider theme={getTheme(themeMode, themeToggler)}>
                {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
                <CssBaseline />
                <Paper elevation={0}>{children}</Paper>
            </ThemeProvider>
        </ThemeModeContext.Provider>
        </ConfirmProvider>
    );
}

Page.propTypes = {
    children: PropTypes.node.isRequired,
};
